<script lang="ts">
  import { initBouncerValidation } from '$src/js/modules/form-validation/init-bouncer';
  import List from '$src/js/modules/participants/templates/List.svelte';
  import Success from '$src/js/modules/participate-event/templates/Success.svelte';
  import Checkbox from '$src/js/modules/ui/components/form/Checkbox.svelte';
  import DbrContact from '$src/js/modules/ui/components/user/DbrContact.svelte';
  import type { ResponseStatus } from '$src/types/forms';
  import type { UrlType, UserType } from '$src/types/user';
  import jump from 'jump.js';
  import { onMount } from 'svelte';

  const {
    user,
    profileUrl,
  }: {
    user: UserType;
    profileUrl: UrlType;
  } = $props();

  let participantId = $state<number | null>(null);
  let participantName = $state('');

  const handleParticipantSelect = (id: number | null, name: string) => {
    participantId = id;
    participantName = name;
  };
  let errorMessage = $state('');
  let loading = $state(false);
  let termsChecked = $state(false);
  let submitted = $state(false);

  const handleFormSubmit = async (event: Event) => {
    const form = event.target as HTMLFormElement;

    if (
      !(form instanceof HTMLFormElement) ||
      form.dataset.validateForm !== 'rdrParticipant'
    ) {
      return;
    }

    event.preventDefault();
    loading = true;

    const formData = new FormData();
    formData.append('eventId', window.currentEvent.id.toString());
    formData.append('participantId', participantId?.toString() ?? '');

    try {
      await new Promise((resolve) => setTimeout(resolve, 400));
      const response = await fetch('/api/activities/dbr/participate/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': window.csrfTokenValue,
        },
        body: JSON.stringify(Object.fromEntries(formData.entries())),
      });

      const responseData = (await response.json()) as {
        status: ResponseStatus;
        message: string;
      };

      if (!response.ok || responseData.status !== 'success') {
        errorMessage =
          'Sorry, het is niet gelukt om te deelnemen. Probeer het opnieuw.';

        if (
          responseData.status === 'fail' &&
          responseData.message === 'participant_already_registered'
        ) {
          errorMessage = `Het lijkt erop dat ${participantName} al ingeschreven is voor dit evenement. Je kunt dit controleren in je profiel onder <a href="${profileUrl}" class="text-white-1000 underline">Mijn profiel</a> onder Mijn inschrijvingen.`;
        }

        loading = false;
        return;
      }

      jump('body');

      submitted = true;
      loading = false;
    } catch {
      errorMessage =
        'Sorry, het is niet gelukt om te deelnemen. Probeer het opnieuw.';
      loading = false;
    }
  };

  onMount(() => {
    initBouncerValidation();

    document.addEventListener('bouncerFormValid', (e) => {
      void handleFormSubmit(e);
    });
  });
</script>

{#if submitted}
  <Success />
{:else}
  <form
    class={`2xl:fluid-gap-[48] flex flex-col gap-8 lg:gap-12 ${loading ? 'loading-box loading-box--bottom' : ''}`}
    data-validate-form="rdrParticipant"
  >
    <h1 class="heading-2xl">Aanmelden als deelnemer</h1>

    <div class="2xl:fluid-gap-[24] flex flex-col gap-6">
      <h2 class="text-28px">Contactpersoon</h2>
      <DbrContact {user} {profileUrl} />
    </div>

    <div class="2xl:fluid-gap-[16] flex flex-col gap-4">
      <h2 class="text-28px">Deelnemer</h2>
      <div class="text-14px leading-[180%]">
        <p>
          Maak of kies hieronder de deelnemer die je wilt inschrijven. Wil je
          meerdere deelnemers inschrijven? Vul dit formulier dan meerdere keren
          in.
        </p>
      </div>
      <List
        selectable={true}
        theme="green"
        onSelect={handleParticipantSelect}
      />
      <input type="hidden" required bind:value={participantId} />
    </div>

    <div class="2xl:fluid-gap-[24] flex flex-col gap-6">
      <Checkbox
        id="terms"
        name="terms"
        theme="green"
        borderColor="white-600"
        required={true}
        bind:checked={termsChecked}
        label={`Ja, ik ga akkoord met de <a href="${window.termsUrl}" class="underline link-decoration-green underline-offset-[20%]">deelnemersvoorwaarden</a> *`}
      />
    </div>

    {#if errorMessage}
      <div
        class="text-14px 2xl:fluid-p-[16] border-error border-1 p-4 leading-[180%] font-medium"
      >
        {@html errorMessage}
      </div>
    {/if}
    <button class="btn-green-large">Voltooi inschrijving</button>
  </form>
{/if}
